import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoPersonSharp } from "react-icons/io5";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { notify } from "../../common/Toast";
import axiosInstance from "../../api/axiosInstance";

const ViewDetails = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const siteData = state?.siteData;
  const [tab, setTab] = useState("overview");
  const [transaction, setTransaction] = useState({
    windowQuotation: [],
    manualQuotation: [],
  });
  const [showWindowQuotation, setShowWindowQuotation] = useState(true);
  const [showManualQuotation, setShowManualQuotation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOwner, setShowOwner] = useState(true);
  const [showArchitect, setShowArchitect] = useState(false);
  const [showFabricator, setShowFabricator] = useState(false);

  const fetchTransaction = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get(
        `SiteDetail/GetBySite/${siteData.site_Id}`
      );
      setTransaction(data || { windowQuotation: [], manualQuotation: [] });
    } catch (err) {
      notify("Error fetching transaction data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      tab === "transaction" &&
      transaction.windowQuotation?.length === 0 &&
      transaction.manualQuotation?.length === 0
    ) {
      fetchTransaction();
    }
  }, [tab]);

  return (
    <div className="rounded-md bg-white shadow-md border px-5 pt-6 pb-8 ">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-bold">Site Details</h1>
        <button
          className="px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-secondary"
          onClick={() => navigate("/transaction/sites")}
        >
          Back
        </button>
      </div>

      <div className="flex gap-6 border-b mb-6">
        <button
          className={`py-2 px-4 border-b-2 ${
            tab === "overview"
              ? "border-primary text-primary font-semibold"
              : "text-gray-500"
          }`}
          onClick={() => setTab("overview")}
        >
          Overview
        </button>
        <button
          className={`py-2 px-4 border-b-2 ${
            tab === "transaction"
              ? "border-primary text-primary font-semibold"
              : "text-gray-500"
          }`}
          onClick={() => setTab("transaction")}
        >
          Transaction
        </button>
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col items-center md:items-start gap-4">
            <div className="bg-gray-200 p-5 rounded-full text-primary">
              <IoPersonSharp className="w-10 h-10" />
            </div>

            <div className="w-full space-y-2 text-sm text-gray-700">
              {[
                { label: "Site Name", value: siteData.site_Name },
                { label: "Site Type", value: siteData.site_Type },
                { label: "Mobile No", value: siteData.mobile_No },
                { label: "Address", value: siteData.address },
                { label: "Remark", value: siteData.Remark },
                { label: "Location Link", value: siteData.location_Link },
                {
                  label: "Sale Person",
                  value: siteData.sale_Person_Name || "-",
                },
              ].map((item, idx) => (
                <div key={idx} className="flex text-base ">
                  <div className="w-40 font-semibold text-gray-800 text-lg">
                    {item.label}:
                  </div>
                  <div className="text-gray-700 text-lg">{item.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4 text-sm text-gray-700">
            {[
              {
                title: "Owner Details",
                isOpen: showOwner,
                setOpen: setShowOwner,
                data: [
                  { label: "Name", value: siteData.owner_Name },
                  { label: "Contact No", value: siteData.owner_Contact_No },
                  { label: "Email", value: siteData.owner_Email_Id },
                  { label: "Firm", value: siteData.owner_Firm_Name },
                  {
                    label: "Office Address",
                    value: siteData.owner_Office_Address,
                  },
                ],
              },
              {
                title: "Architect Details",
                isOpen: showArchitect,
                setOpen: setShowArchitect,
                data: [
                  { label: "Name", value: siteData.architect_Name },
                  { label: "Contact No", value: siteData.architect_Contact_No },
                  { label: "Email", value: siteData.architect_Email_Id },
                  { label: "Firm", value: siteData.architect_Firm_Name },
                  {
                    label: "Office Address",
                    value: siteData.architect_Office_Address,
                  },
                ],
              },
              {
                title: "Fabricator Details",
                isOpen: showFabricator,
                setOpen: setShowFabricator,
                data: [
                  { label: "Name", value: siteData.fabricator_Name },
                  {
                    label: "Contact No",
                    value: siteData.fabricator_Contact_No,
                  },
                  { label: "Email", value: siteData.fabricator_Email_Id },
                  { label: "Firm", value: siteData.fabricator_Firm_Name },
                  {
                    label: "Office Address",
                    value: siteData.fabricator_Office_Address,
                  },
                ],
              },
            ].map(({ title, isOpen, setOpen, data }, idx) => (
              <div key={idx}>
                <div
                  className="flex justify-between items-center bg-gray-100 hover:bg-gray-200 px-4 py-3 rounded-md cursor-pointer transition"
                  onClick={() => setOpen(!isOpen)}
                >
                  <h2 className="font-semibold text-lg">{title}</h2>
                  {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                </div>
                {isOpen && (
                  <div className="px-4 py-2 space-y-2 bg-white border border-gray-200 rounded-b-md">
                    {data.map((d, i) => (
                      <div key={i} className="flex">
                        <div className="w-40 font-semibold">{d.label}:</div>
                        <div>{d.value}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "transaction" && (
        <div className="mt-4 space-y-6">
          {loading ? (
            <p>Loading transactions...</p>
          ) : (
            <>
              <div className="border rounded-md shadow-sm">
                <div
                  className="flex justify-between items-center bg-gray-100 px-4 py-3 cursor-pointer"
                  onClick={() => setShowWindowQuotation((prev) => !prev)}
                >
                  <div className="flex gap-4">
                    <h3 className="text-primary font-semibold">
                      Window Quotation
                    </h3>
                    <button
                      className="text-sm bg-primary text-white px-3 py-1 rounded"
                      onClick={() =>
                        navigate("/quotes/window-quotation/active-details", {
                          state: { selectedSite: siteData },
                        })
                      }
                    >
                      + Create New
                    </button>
                  </div>

                  {showWindowQuotation ? <FaChevronUp /> : <FaChevronDown />}
                </div>
                {showWindowQuotation &&
                transaction.windowQuotation?.length > 0 ? (
                  <div className="px-4 py-2">
                    <table className="w-full text-sm">
                      <thead className="text-left border-b">
                        <tr>
                          <th className="py-2">Quotation No</th>
                          <th>Quotation Date</th>
                          <th>Site Name</th>
                          <th>Stage Name</th>
                          <th>Sale Person Name</th>
                          <th>Total Sqft</th>
                          <th>Quote Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transaction.windowQuotation.map((item, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-2">{item.quotation_No}</td>
                            <td>{item.quotation_Date.split("T")[0]}</td>
                            <td className="py-2">{item.site_Name}</td>
                            <td className="py-2">{item.stage_Name}</td>
                            <td className="py-2">{item.sale_Person_Name}</td>
                            <td>{item.total_Sqft}</td>
                            <td>₹{item.quote_Amount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  showWindowQuotation && (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      No window quotation available.
                    </div>
                  )
                )}
              </div>

              <div className="border rounded-md shadow-sm">
                <div
                  className="flex justify-between items-center bg-gray-100 px-4 py-3 cursor-pointer"
                  onClick={() => setShowManualQuotation((prev) => !prev)}
                >
                  <div className="flex gap-4">
                    <h3 className="text-primary font-semibold">
                      Manual Quotation
                    </h3>
                    <button
                      className="text-sm bg-primary text-white px-3 py-1 rounded"
                      onClick={() =>
                        navigate("/quotes/manual-quotation/manual-details", {
                          state: { selectedSite: siteData },
                        })
                      }
                    >
                      + Created New
                    </button>
                  </div>
                  {showManualQuotation ? <FaChevronUp /> : <FaChevronDown />}
                </div>
                {showManualQuotation &&
                transaction.manualQuotation?.length > 0 ? (
                  <div className="px-4 py-2">
                    <table className="w-full text-sm">
                      <thead className="text-left border-b">
                        <tr>
                          <th className="py-2">Quotation No</th>
                          <th>Quotation Date</th>
                          <th className="py-2">Site Name</th>
                          <th className="py-2">Stage Name</th>
                          <th className="py-2">Sale Person Name</th>
                          <th className="py-2">Company Name</th>
                          <th>Net Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transaction.manualQuotation.map((item, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-2">{item.quotation_No}</td>
                            <td>{item.quotation_Date.split("T")[0]}</td>
                            <td className="py-2">{item.site_Name}</td>
                            <td className="py-2">{item.stage_Name}</td>
                            <td className="py-2">{item.sale_Person_Name}</td>
                            <td className="py-2">{item.company_Name}</td>
                            <td>₹{item.net_Amount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  showManualQuotation && (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      No manual quotation available.
                    </div>
                  )
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ViewDetails;
